import React, { useEffect } from "react";
import { Box, Typography, Button, Card, CardContent, Grid, CircularProgress } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useParams } from "react-router-dom";
import { useTQuery } from "../../utils/useTQuery";
import { setAllowRefetch } from "../../slice/userSlice";
import { useDispatch } from "react-redux";

const PaymentResultScreen = () => {
  const { transactionID } = useParams();
  const dispatch = useDispatch();
  const paymentIntentId = new URLSearchParams(window.location.search).get("payment_intent");
  const theme = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(setAllowRefetch(true));
  }, []);

  // Fetch transaction data
  const { data, isLoading } = useTQuery(
    ["paymentTransaction", transactionID],
    transactionID ? `/payment/payment-intent?${new URLSearchParams({ ID: transactionID, paymentIntentId }).toString()}` : null,
    "get",
    !!transactionID
  );

  // Show loading screen while fetching data
  if (isLoading || data?.STATUS === undefined) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: theme.palette.background.default,
          textAlign: "center",
          padding: 3,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ marginTop: 2, fontWeight: "bold" }}>
          Waiting for payment confirmation...
        </Typography>
      </Box>
    );
  }

  // Use transaction data directly
  const transactionData = data?.STATUS ? data?.OUTPUT || {} : {};
  console.log("transactionData", transactionData)
  // Define success and failure UI
  const paymentStatusUI = transactionData.status === "succeeded" ? {
    icon: <CheckCircleIcon sx={{ fontSize: 60, color: theme.palette.success.main }} />,
    title: "Payment Successful 🎉",
    message: "Thank you for your payment! Your transaction was processed successfully.",
    bgColor: theme.palette.success.light
  } : {
    icon: <ErrorIcon sx={{ fontSize: 60, color: theme.palette.error.main }} />,
    title: "Payment Failed ❌",
    message: "Unfortunately, your payment could not be completed.",
    bgColor: theme.palette.error.light
  };

  // Extract transaction details
  const {
    status, notes, paymentDate, transactionId, amount, paymentMethod, currency
  } = transactionData;


  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: paymentStatusUI.bgColor,
        padding: 2,
      }}
    >
      <Card
        sx={{
          maxWidth: 600,
          textAlign: "center",
          padding: 3,
          borderRadius: 3,
          boxShadow: theme.shadows[4],
        }}
      >
        <CardContent>
          {paymentStatusUI.icon}
          <Typography variant="h5" sx={{ marginY: 2 }}>
            {paymentStatusUI.title}
          </Typography>
          <Typography>{paymentStatusUI.message}</Typography>

          {transactionData.status !== "succeeded" && transactionData.notes && (
            <Typography
              sx={{
                marginTop: 2,
                fontWeight: "bold",
                color: theme.palette.error.dark,
              }}
            >
              Reason: {transactionData.notes}
            </Typography>
          )}

          {/* Invoice-like Transaction Details */}
          <Grid container spacing={2} sx={{ marginTop: 3 }}>

            <Grid item xs={6} sx={{ textAlign: "left" }}>
              <Typography variant="body1" color="textSecondary">Payment Method:</Typography>
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                {paymentMethod || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={6} sx={{ textAlign: "left" }}>
              <Typography variant="body1" color="textSecondary">Transaction ID:</Typography>
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                {transactionId}
              </Typography>
            </Grid>
            <Grid item xs={6} sx={{ textAlign: "left" }}>
              <Typography variant="body1" color="textSecondary">Amount:</Typography>
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                {currency.toUpperCase()} {amount}
              </Typography>
            </Grid>
            <Grid item xs={6} sx={{ textAlign: "left" }}>
              <Typography variant="body1" color="textSecondary">Payment Date:</Typography>
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                {new Date(paymentDate).toLocaleString()}
              </Typography>
            </Grid>
            <Grid item xs={6} sx={{ textAlign: "left" }}>
              <Typography variant="body1" color="textSecondary">Status:</Typography>
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                {status === "succeeded" ? "Success" : "Failed"}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>

        <Button
          variant="contained"
          color="primary"
          fullWidth
          sx={{ marginTop: 3 }}
          onClick={() => navigate("/facility/timesheet/clinician-details/" + transactionData?.clinicianMappedJobId)}
        >
          Return to Previous
        </Button>
      </Card>
    </Box>
  );
};

export default PaymentResultScreen;
